/**
 * PR B — Access wall.
 *
 * Replaces the entire chat panel when the backend returns a 403 with
 * `error: 'access_not_provisioned'` or `error: 'tenant_suspended'`. The
 * user has signed in successfully via Microsoft, but their Entra OID does
 * not match any provisioned row in the `users` table.
 *
 * Pre-PR-B (entitlement not enforced): this wall is never shown.
 */
import React from 'react';

export type AccessWallReason = 'not_provisioned' | 'suspended';

interface Props {
  reason: AccessWallReason;
  message: string;
  contactEmail: string;
}

export function AccessRequestWall({ reason, message, contactEmail }: Props): JSX.Element {
  const subject = encodeURIComponent(
    reason === 'suspended'
      ? 'Practice IQ Reporting — Access Reactivation Request'
      : 'Practice IQ Reporting — Access Request'
  );
  const title = reason === 'suspended' ? 'Access suspended' : 'Welcome to Practice IQ Reporting';
  const cta = reason === 'suspended' ? 'Contact support' : `Email ${contactEmail}`;

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.title}>{title}</div>
        <p style={s.body}>{message}</p>
        <a href={`mailto:${contactEmail}?subject=${subject}`} style={s.cta}>{cta}</a>
        <div style={s.footer}>We typically respond within one business day, Sydney time.</div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '32px 16px', height: '100%',
  },
  card: {
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    maxWidth: 420,
    width: '100%',
    padding: 24,
    textAlign: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1F3864',
    marginBottom: 12,
  },
  body: {
    fontSize: 13,
    color: '#1f1f1f',
    lineHeight: 1.5,
    margin: '0 0 20px 0',
  },
  cta: {
    display: 'inline-block',
    background: '#1F3864',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: 4,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 13,
  },
  footer: {
    fontSize: 11,
    color: '#888',
    marginTop: 20,
  },
};
